import { inject } from '@angular/core';

import type { CanvasElementId, ColumnId, PropsOf, WithSlots } from '@blaze-runtime/engine-domain';
import {
  getFormGroupLikeWrapper,
  getFormGroupLikeWrapperMainFormId,
} from '@blaze-runtime/engine-domain';
import {
  type CanvasElementPropertiesWithData,
  getActiveBinding,
  type IPropEditorResolver,
  type PropertiesConfigItemDefinition,
  type PropertiesConfigSubsection,
} from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { getColumnKey } from '@blaze-shared/schema-domain';
import { type SelectOption } from '@blaze-shared/shared-util';

import { RuntimeFacade } from '../../+state';
import { hasDataProps } from '../../canvas-elements/element-model/element-props-guards';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';
import { isElementChildOfAtSlot, isElementPreceding } from '../../utils/layout/relationship';

import { getWizardRequiredQuestionColumns } from './wizard-question-steps';
import {
  WIZARD_CONFIRMATION_TYPES,
  WIZARD_DEFAULT_CONFIRMATION_LABEL,
  WIZARD_DEFAULT_CONFIRMATION_MESSAGE,
  WIZARD_DEFAULT_CONFIRMATION_TITLE,
  WIZARD_STEP_TYPES,
} from './wizard.constants';
import {
  type WizardConfirmationType,
  type WizardPropsModel,
  type WizardStep,
  type WizardStepType,
} from './wizard.models';
import { resolveWizardDatasetId } from './wizard.utils';

export function wizardStepsField(
  path: string,
  label: string,
): PropertiesConfigItemDefinition<'collectionList'> {
  return {
    path,
    inputType: 'collectionList',
    label,
    inputOptions: {
      fieldAction: 'COLLECTION_EDIT_STEP',
      fieldPaneTitle: (step: WizardStep) =>
        step.type === 'confirmation' ? 'Confirmation' : 'Step',
      fieldConfig: (index: number, _model: WizardPropsModel, steps: WizardStep[] | undefined) =>
        steps?.[index].type === 'confirmation'
          ? wizardConfirmationSubsections(path, index)
          : wizardStepSubsections(path, index),
      fieldFactory: (): WizardStep => ({
        id: crypto.randomUUID(),
        type: 'data',
        label: '',
      }),
      newItemPosition: (steps: WizardStep[]) =>
        steps.findIndex((step) => step.type === 'confirmation'),
      selectable: true,
    },
  };
}

function wizardStepSubsections(path: string, index: number): PropertiesConfigSubsection[] {
  return [
    epc.subsections.subsection(
      `${path}.${index}`,
      '',
      [
        epc.fields.textInput(`${path}.${index}.label`, 'Label'),
        epc.utils.observedField(
          epc.fields.selectFieldFromList(`${path}.${index}.type`, 'Type', WIZARD_STEP_TYPES, {
            showSelection: true,
            allowNone: false,
            optionsFilter: ((
              model: WizardPropsModel,
              options: SelectOption<WizardStepType, string>[],
            ): SelectOption<WizardStepType, string>[] =>
              options.filter(({ value }) =>
                value === 'review'
                  ? !model.content.steps?.items.some(
                      ({ type }, i) => i !== index && type === 'review',
                    )
                  : value === 'confirmation'
                    ? index === (model.content.steps?.items || []).length - 1 &&
                      !model.content.steps?.items.some(
                        ({ type }, i) => i !== index && type === 'confirmation',
                      )
                    : true,
              )) as (model: any, options: SelectOption[]) => SelectOption[],
          }),
          (model: WizardPropsModel, value: WizardStepType) =>
            value === 'confirmation'
              ? {
                  [`${path}.${index}.label`]: WIZARD_DEFAULT_CONFIRMATION_LABEL,
                  ...(model.content.steps?.items[index].postAction?.actionType === undefined
                    ? {
                        [`${path}.${index}.postAction.actionType`]: 'Message',
                      }
                    : {}),
                  ...(model.content.steps?.items[index].postAction?.title === undefined
                    ? {
                        [`${path}.${index}.postAction.title`]: WIZARD_DEFAULT_CONFIRMATION_TITLE,
                      }
                    : {}),
                  ...(model.content.steps?.items[index].postAction?.message === undefined
                    ? {
                        [`${path}.${index}.postAction.message`]:
                          WIZARD_DEFAULT_CONFIRMATION_MESSAGE.Message,
                      }
                    : {}),
                }
              : {},
        ),
        epc.fields.actionConfig(`${path}.${index}.nextAction`, {
          workflowIdPath: (_action) => `${path}.${index}.nextAction.workflowId`,
          basePath: `${path}.${index}.nextAction`,
          actionGenerationType: 'step',
        }),
      ],
      { hideLabel: true, classes: ['break-top'] },
    ),
    epc.subsections.subsection(
      `${path}.${index}`,
      'Options',
      [
        ...epc.utils.conditionalFields(
          wizardRequiredAndVisibilityFields(path, index),
          `${path}.${index}.type`,
          (type: WizardStepType) => type !== 'data',
        ),
        ...epc.utils.conditionalFields(
          [
            epc.fields.textInfo('', 'Forms for review:', { header: true }),
            epc.fields.toggleArrayField(
              `${path}.${index}.reviewForms`,
              '',
              (
                props: PropsOf<'wizard'> & {
                  root: WithSlots<ColumnId> & { elementId: CanvasElementId };
                },
              ) => {
                if (!props.wrapper) {
                  return [];
                }
                const runtimeFacade = inject(RuntimeFacade);
                const formIds =
                  'refs' in props.wrapper
                    ? getFormGroupLikeWrapper(props.wrapper.refs).map(({ formId }) => formId)
                    : [];
                const orderedFormIds = props.root.slots.ids
                  .map((slotId) => ({
                    slotId,
                    formIds: formIds.filter((formId) =>
                      isElementChildOfAtSlot(
                        runtimeFacade.elementsValue,
                        formId,
                        props.root.elementId,
                        slotId,
                      ),
                    ),
                  }))
                  .map(({ slotId, formIds }) => ({
                    slotId,
                    formIds: formIds.sort((a, b) =>
                      isElementPreceding(runtimeFacade.elementsValue, a, b, props.root.elementId)
                        ? -1
                        : 1,
                    ),
                  }));
                return orderedFormIds.flatMap(({ slotId, formIds }) =>
                  formIds.map((formId) => {
                    const stepIndex = props.root.slots.ids.indexOf(slotId);
                    const form = runtimeFacade.elementsValue[formId];
                    const name = hasDataProps(form)
                      ? getActiveBinding(form.props.data, 'value')?.dataSource?.name
                      : undefined;
                    return {
                      label: `${name ?? '???'} (${props.content.steps?.items[stepIndex].label ?? '???'})`,
                      value: formId,
                    };
                  }),
                );
              },
            ),
            epc.fields.visibilityField('', `${path}.${index}`),
          ],
          `${path}.${index}.type`,
          (type: WizardStepType) => type !== 'review',
        ),
        ...epc.utils.conditionalFields(
          [epc.fields.textInfo('', 'Please reopen this window!', { header: true })],
          `${path}.${index}.type`,
          (type: WizardStepType) => type !== 'confirmation',
        ),
      ],
      { classes: ['break-bottom'] },
    ),
  ];
}

/** Why the "Required step" toggle is locked on, when it is. */
const REQUIRED_COLUMN_MESSAGE =
  'The data source requires this column, so its question cannot be skipped';
const MAIN_FORM_STEP_MESSAGE = 'Step with the main Form must be required';

/** The panel model of the wizard the step belongs to, as the toggle's predicate reads it. */
type WizardStepOptionsModel = PropsOf<'wizard'> &
  CanvasElementPropertiesWithData & {
    root: WithSlots<ColumnId> & { elementId: CanvasElementId };
  };

/**
 * "Required step" toggle + visibility conditions - shared by Form-mode steps and
 * Questionnaire questions (both live in content.steps.items). Two things lock the toggle on,
 * and locking it is the whole guarantee that a step cannot be skipped: `conditionalField`
 * below hides the visibility conditions while `required` is true, so nothing can hide a
 * locked step, and the runtime's Next refuses to advance past its empty required field.
 */
export function wizardRequiredAndVisibilityFields(
  path: string,
  index: number,
): PropertiesConfigItemDefinition[] {
  return [
    ...epc.utils.disabledFields(
      [epc.fields.toggleField(`${path}.${index}.required`, 'Required step')],
      (props: WizardStepOptionsModel) => {
        // the column's reason comes first: it is true of the question itself and never
        // changes, while the main form's travels between questions as they are reordered
        if (asksAboutRequiredColumn(props, index)) {
          return REQUIRED_COLUMN_MESSAGE;
        }
        return holdsMainForm(props, index) ? MAIN_FORM_STEP_MESSAGE : '';
      },
    ),
    epc.utils.conditionalField(
      epc.fields.visibilityField('', `${path}.${index}`),
      `${path}.${index}.required`,
      (required: WizardStep['required']) => !!required,
    ),
  ];
}

/**
 * Whether the question at `index` asks about a column its dataset requires. Questionnaire
 * mode only: a Form-mode step carries no `columnKey`, so this is false for every one of them.
 *
 * Read through the same dataset resolution and column filter the Add Question dialog uses -
 * a knob that locked on columns the dialog never offered would be locking on a different
 * dataset's requirements.
 */
function asksAboutRequiredColumn(props: WizardStepOptionsModel, index: number): boolean {
  const columnKey = props.content?.steps?.items[index]?.columnKey;
  if (!columnKey) {
    return false;
  }
  const schemaService = inject(SchemaService);
  const datasetId = resolveWizardDatasetId(schemaService, props);
  const schema = datasetId ? schemaService.getDatasetSchema(datasetId) : undefined;
  return getWizardRequiredQuestionColumns(schema?.columns ?? []).some(
    (column) => getColumnKey(column) === columnKey,
  );
}

/**
 * Whether the step at `index` owns the slot holding the wrapper's main form. In questionnaire
 * mode the main form is a role one question's form holds, so which step this is moves as
 * questions are reordered and deleted.
 */
function holdsMainForm(props: WizardStepOptionsModel, index: number): boolean {
  const mainFormId =
    props.wrapper && 'refs' in props.wrapper
      ? getFormGroupLikeWrapperMainFormId(props.wrapper.refs).mainFormId
      : undefined;
  if (!mainFormId) {
    return false;
  }
  return isElementChildOfAtSlot(
    inject(RuntimeFacade).elementsValue,
    mainFormId,
    props.root.elementId,
    props.root.slots.ids[index],
  );
}

export function wizardConfirmationSubsections(
  path: string,
  index: number,
): PropertiesConfigSubsection[] {
  return [
    epc.subsections.subsection(
      `${path}.${index}`,
      '',
      [
        epc.fields.textInfo('', 'Select redirection method', {
          header: true,
          classes: ['break-top'],
        }),
        epc.utils.observedField(
          epc.fields.radiosField(
            `${path}.${index}.postAction.actionType`,
            '',
            WIZARD_CONFIRMATION_TYPES,
            { hideLabel: true },
          ),
          (model: WizardPropsModel, value: WizardConfirmationType) => {
            return {
              [`${path}.${index}.postAction.message`]: WIZARD_DEFAULT_CONFIRMATION_MESSAGE[value],
              ...(value === 'Button' &&
              model.content.steps?.items[index].postAction?.buttonLabel === undefined
                ? {
                    [`${path}.${index}.postAction.buttonLabel`]: 'Continue',
                  }
                : {}),
            };
          },
        ),
      ],
      { hideLabel: true, classes: ['break-top'] },
    ),
    epc.subsections.subsection(
      `${path}.${index}`,
      'Loading Message',
      [...wizardConfirmationFields(path, index)],
      {
        classes: ['break-bottom'],
        hideWhen: (_: string, __: IPropEditorResolver, props: WizardPropsModel) =>
          props.content.steps?.items[index].postAction?.actionType !== 'Page',
      },
    ),
    epc.subsections.subsection(
      `${path}.${index}`,
      'Confirmation Message',
      [...wizardConfirmationFields(path, index)],
      {
        classes: ['break-bottom'],
        hideWhen: (_: string, __: IPropEditorResolver, props: WizardPropsModel) =>
          props.content.steps?.items[index].postAction?.actionType === 'Page',
      },
    ),
  ];
}

function wizardConfirmationFields(path: string, index: number): PropertiesConfigItemDefinition[] {
  return [
    ...epc.utils.conditionalFields(
      [
        epc.fields.selectFieldFromApi(
          `${path}.${index}.postAction.page`,
          'Redirect to',
          'LIST_PAGES',
          { allowNone: false, showSelection: true, placeholder: 'Select page' },
        ),
        epc.utils.separator(),
      ],
      `${path}.${index}.postAction.actionType`,
      (type: WizardConfirmationType) => type === 'Message',
    ),
    epc.fields.textAreaField(`${path}.${index}.postAction.title`, 'Title', { rows: 3 }),
    epc.fields.textAreaField(`${path}.${index}.postAction.message`, 'Message', { rows: 6 }),
    ...epc.utils.conditionalFields(
      [epc.fields.textInput(`${path}.${index}.postAction.buttonLabel`, 'Button Label')],
      `${path}.${index}.postAction.actionType`,
      (type: WizardConfirmationType) => type !== 'Button',
    ),
    epc.fields.uploadField(`${path}.${index}.postAction.image`, 'Type URL or upload image'),
  ];
}
